import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extendedTextColors } from '../../../../constants';
import { useAnalyticsContext } from '../../../../context/analytics-context';
import { useActiveCanvas } from '../../../../hooks';
import { getGroupedTextObject } from '../../../../utils';
import styles from '../font-drawer.module.scss';
import { hexToColorNameMap } from '../utils/hex-to-color-name-map';
import { onChangeTextColor } from '../utils/on-change-text-color';
import { ColorOption } from './color-option';
import { useHistoryTransaction, useHistoryTransactionErrorToast } from '../../../../features/edit-history/edit-history';

type ColorTabProps = {
  scrollable?: boolean;
  /** When false, suppresses the visible label above the colour swatches. Defaults to true. */
  showLabel?: boolean;
};

export const ColorTab = ({ scrollable, showLabel = true }: ColorTabProps): React.ReactElement => {
  const canvas = useActiveCanvas();
  const initialHexColor = getGroupedTextObject(canvas?.current)?.fill;
  const initialColor = hexToColorNameMap[initialHexColor as string];
  const [selectedColor, setSelectedColor] = useState(initialColor || '');
  const { updateEditFormats } = useAnalyticsContext();
  const showHistoryTransactionError = useHistoryTransactionErrorToast();
  const { t } = useTranslation();
  const runHistoryTransaction = useHistoryTransaction();

  const onColorClick = async (color: string): Promise<void> => {
    const targetCanvas = canvas?.current;
    if (!targetCanvas) return;

    const changeResult = { wasApplied: false };
    try {
      await runHistoryTransaction(targetCanvas, () => {
        changeResult.wasApplied = onChangeTextColor(color, canvas);
      });
      if (!changeResult.wasApplied) return;
      setSelectedColor(color);
      updateEditFormats({ color });
    } catch {
      showHistoryTransactionError();
    }
  };

  return (
    <ColorOption
      testId="color-tab"
      ariaLabel={t('fontDrawer.textColor') as string}
      label={showLabel ? (t('fontDrawer.textColor') as string) : undefined}
      addClass={styles['color-option']}
      colors={extendedTextColors}
      selectedColor={selectedColor}
      scrollable={scrollable}
      buttonGroupClick={(color) => onColorClick(color)}
    />
  );
};
